// src/layouts/alerts/alertConfiguration/components/AddAlertPopup.js
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import MDButton from "components/MDButton";
import { useSelector, useDispatch } from "react-redux";
import { setOpenPopup, setAddPopup, setRefresh } from "features/alert/alertSlice";
import { setAlertConfiguration } from "config/apis";
import { fetchAlertConfigDetails } from "features/alert/alertThunk";
import { ignition } from "config/apis";

const AddAlertPopup = () => {
  const [formData, setFormData] = useState({
    alertValue: "",
    mobileNumbers: [],
    emails: [],
    startTime: "",
    endTime: "",
  });

  const [emailInput, setEmailInput] = useState("");
  const [mobileInput, setMobileInput] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const dispatch = useDispatch();
  const selectedAlert = useSelector((state) => state.alerts.selectedAlert);
  const addPopup = useSelector((state) => state.alerts.addPopup);

  const ignitionAlertIds = ["14", "9", "6", "10", "7", "8"];

  const isIgnitionAlert = ignitionAlertIds.includes(
    String(selectedAlert?.alertId)
  );

  const knotsToKmh = (knots) => {
    if (knots === "" || knots === null || knots === undefined) return "";

    return Math.round(Number(knots) * 1.852);
  };

  const handleClose = () => dispatch(setAddPopup(false));

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ================= VALIDATION FUNCTIONS =================
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidMobile = (mobile) => {
    const mobileRegex = /^[0-9]{10}$/; // 10 digit only
    return mobileRegex.test(mobile);
  };

  // ================= EMAIL =================
  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();

    if (!trimmedEmail) return;

    if (!isValidEmail(trimmedEmail)) {
      setSnackbar({
        open: true,
        message: "Please enter a valid Email ID",
        severity: "error",
      });
      return;
    }

    if (formData.emails.includes(trimmedEmail)) {
      setSnackbar({
        open: true,
        message: "Email already added",
        severity: "warning",
      });
      return;
    }

    setFormData({
      ...formData,
      emails: [...formData.emails, trimmedEmail],
    });

    setEmailInput("");
  };

  const handleDeleteEmail = (emailToDelete) => {
    setFormData({
      ...formData,
      emails: formData.emails.filter((email) => email !== emailToDelete),
    });
  };

  // ================= MOBILE =================
  const handleAddMobile = () => {
    const trimmedMobile = mobileInput.trim();

    if (!trimmedMobile) return;

    if (!isValidMobile(trimmedMobile)) {
      setSnackbar({
        open: true,
        message: "Mobile number must be 10 digits",
        severity: "error",
      });
      return;
    }

    if (formData.mobileNumbers.includes(trimmedMobile)) {
      setSnackbar({
        open: true,
        message: "Mobile number already added",
        severity: "warning",
      });
      return;
    }

    setFormData({
      ...formData,
      mobileNumbers: [...formData.mobileNumbers, trimmedMobile],
    });

    setMobileInput("");
  };

  const handleDeleteMobile = (numberToDelete) => {
    setFormData({
      ...formData,
      mobileNumbers: formData.mobileNumbers.filter(
        (num) => num !== numberToDelete
      ),
    });
  };

  // ================= SUBMIT =================
  const handleFormSubmit = async () => {
    const token = localStorage.getItem("token");

    if (!isIgnitionAlert && !formData.alertValue) {
      setSnackbar({
        open: true,
        message: "Alert Value is required",
        severity: "error",
      });
      return;
    }

    const { startTime, endTime } = formData;

    if ((startTime && !endTime) || (!startTime && endTime)) {
      setSnackbar({
        open: true,
        message: "Both Start Time and End Time are required if you are setting a time range",
        severity: "error",
      });
      return;
    }

    const payload = [
      {
        thingId: selectedAlert?.thingId || "",
        mobileNumber: formData.mobileNumbers,
        emailId: formData.emails,
        // alertValue: formData.alertValue,
        // alertValue: isIgnitionAlert ? "" : formData.alertValue,
        alertValue: isIgnitionAlert
          ? ""
          : knotsToKmh(formData.alertValue),
        alertID: selectedAlert?.alertId || "",
        alertEnabled: "true",
        alertValueOption: "true",
        startTime: formData.startTime,
        endTime: formData.endTime,
      },
    ];

    try {
      const response = await fetch(setAlertConfiguration, {
        method: "POST",
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to save alert configuration");
      }

      const result = await response.json();

      const backendMessage =
        result?.responseData?.[0]?.message ||
        result?.message ||
        "Alert added successfully";

      setSnackbar({
        open: true,
        message: backendMessage,
        severity: "success",
      });

      dispatch(setOpenPopup(false));
      dispatch(setRefresh());
      await dispatch(fetchAlertConfigDetails(selectedAlert?.alertId));

      setFormData({ alertValue: "", mobileNumbers: [], emails: [] });
      handleClose();
    } catch (error) {
      console.error("❌ Error:", error);

      setSnackbar({
        open: true,
        message: "Failed to add alert configuration",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Dialog
        open={addPopup}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", boxShadow: 8 },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#3177e8",
          }}
        >
          <Typography variant="h6" color="white" fontWeight="bold">
            Add Alert Configuration
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

       <DialogContent
         sx={{
           display: "flex",
           flexDirection: "column",
           gap: 3,
           pt: 3,
         }}
       >
         {!isIgnitionAlert && (
           <TextField
             label="Alert Value (knots)"
             value={formData.alertValue}
             onChange={(e) =>
               setFormData({ ...formData, alertValue: e.target.value })
             }
             required
             fullWidth
             InputLabelProps={{ shrink: true }}
             sx={{
               mt: 1,
             }}
           />
         )}

          {/* Mobile Section */}
          {/* <Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="Add Mobile Number"
                value={mobileInput}
                onChange={(e) => setMobileInput(e.target.value)}
                fullWidth
              />
              <IconButton
                onClick={handleAddMobile}
                sx={{
                  bgcolor: "#3177e8",
                  color: "white",
                  "&:hover": { bgcolor: "#255ec7" },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {formData.mobileNumbers.map((number, index) => (
                <Chip
                  key={index}
                  label={number}
                  onDelete={() => handleDeleteMobile(number)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box> */}

          {/* Email Section */}
          <Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="Add Email ID"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                fullWidth
              />
              <IconButton
                onClick={handleAddEmail}
                sx={{
                  bgcolor: "#3177e8",
                  color: "white",
                  "&:hover": { bgcolor: "#255ec7" },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {formData.emails.map((email, index) => (
                <Chip
                  key={index}
                  label={email}
                  onDelete={() => handleDeleteEmail(email)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Start Time"
              type="time"
              value={formData.startTime}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }} // 5 min intervals
            />
            <TextField
              label="End Time"
              type="time"
              value={formData.endTime}
              onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={handleClose} color="secondary" variant="outlined">
            Cancel
          </MDButton>
          <MDButton onClick={handleFormSubmit} color="info" variant="contained">
            Submit
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddAlertPopup;
